import React, { useState, useEffect } from 'react';
import { samlAPI } from '../services/api';

const SAMLConfiguration = ({ appId, onSuccess, onError }) => {
  const [activeTab, setActiveTab] = useState('sp-metadata');
  const [loading, setLoading] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [metadataUrl, setMetadataUrl] = useState('');
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [metadataFile, setMetadataFile] = useState(null);
  const [spMetadata, setSPMetadata] = useState({
    entity_id: '',
    acs_url: '',
    slo_url: '',
    name_id_format: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
    want_assertions_signed: true,
    want_authn_requests_signed: false
  });
  const [newCert, setNewCert] = useState({
    name: '',
    generate: true,
    common_name: '',
    organization: 'IDP',
    validity_days: 365
  });
  const [samlConfig, setSAMLConfig] = useState({
    certificate_id: null,
    sign_assertions: true,
    sign_response: true,
    encrypt_assertions: false,
    encryption_certificate_id: null
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load certificates
      const certsRes = await samlAPI.listCertificates();
      setCertificates(certsRes.certificates || []);
      
      // Load SP metadata if exists
      try {
        const metadataRes = await samlAPI.getSPMetadata(appId);
        setSPMetadata(metadataRes);
        setMetadataUrl(metadataRes.metadata_url || '');
      } catch (err) {
        // SP metadata doesn't exist yet
      }
      
      // Load SAML configuration
      try {
        const configRes = await samlAPI.getSAMLConfig(appId);
        if (configRes) {
          setSAMLConfig({
            certificate_id: configRes.certificate_id,
            sign_assertions: configRes.sign_assertions !== false,
            sign_response: configRes.sign_response !== false,
            encrypt_assertions: configRes.encrypt_assertions || false,
            encryption_certificate_id: configRes.encryption_certificate_id || null
          });
        }
      } catch (err) {
      }
    } catch (err) {
      console.error('Failed to load SAML data:', err);
      onError(err.message || 'Failed to load SAML configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSPMetadata = async () => {
    try {
      setLoading(true);
      
      // Validate certificate selection
      if (!samlConfig.certificate_id) {
        onError('Please select a signing certificate');
        setLoading(false);
        return;
      }
      
      // Save SP metadata
      await samlAPI.updateSPMetadata(appId, spMetadata);
      
      // Save SAML configuration
      await samlAPI.updateSAMLConfig(appId, samlConfig);
      
      onSuccess('SAML configuration saved successfully');
      loadData();
    } catch (err) {
      onError(err.message || 'Failed to save SAML configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCertificate = async () => {
    try {
      setLoading(true);
      await samlAPI.createCertificate({
        ...newCert,
        generate: true
      });
      onSuccess('Certificate generated successfully');
      setNewCert({
        name: '',
        generate: true,
        common_name: '',
        organization: 'IDP',
        validity_days: 365
      });
      loadData();
    } catch (err) {
      onError(err.message || 'Failed to generate certificate');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCertificate = async (certId, certName, type = 'public', password = null) => {
    try {
      const blob = await samlAPI.downloadCertificate(certId, type, password);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      let filename;
      if (type === 'pfx') {
        filename = `${certName.replace(/ /g, '_')}.pfx`;
      } else if (type === 'private') {
        filename = `${certName.replace(/ /g, '_')}_private_key.pem`;
      } else {
        filename = `${certName.replace(/ /g, '_')}_certificate.pem`;
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      const typeLabel = type === 'pfx' ? 'PFX file' : (type === 'private' ? 'Private key' : 'Certificate');
      onSuccess(`${typeLabel} downloaded successfully`);
    } catch (err) {
      onError(err.message || 'Failed to download certificate');
    }
  };

  const handleDeleteCertificate = async (certId) => {
    if (!window.confirm('Are you sure you want to delete this certificate?')) {
      return;
    }
    
    try {
      setLoading(true);
      await samlAPI.deleteCertificate(certId);
      onSuccess('Certificate deleted successfully');
      loadData();
    } catch (err) {
      onError(err.message || 'Failed to delete certificate');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoDetect = async () => {
    if (!spMetadata.entity_id || !spMetadata.acs_url) {
      onError('Please enter Entity ID and ACS URL first');
      return;
    }
    
    setAutoDetecting(true);
    try {
      const response = await samlAPI.autoDetectMetadataUrl(appId, {
        entity_id: spMetadata.entity_id,
        acs_url: spMetadata.acs_url
      });
      
      if (response.suggestions && response.suggestions.length > 0) {
        setMetadataUrl(response.suggestions[0]);
        onSuccess(`Found metadata URL: ${response.suggestions[0]}`);
      } else {
        onError('Could not auto-detect metadata URL');
      }
    } catch (err) {
      onError(err.message);
    } finally {
      setAutoDetecting(false);
    }
  };

  const handleFetchMetadata = async () => {
    if (!metadataUrl) {
      onError('Please enter a metadata URL');
      return;
    }
    
    setFetching(true);
    try {
      const response = await samlAPI.fetchSPMetadata(appId, { metadata_url: metadataUrl });
      onSuccess(`Metadata fetched! Found ${response.attributes_detected} attributes`);
      loadData();
    } catch (err) {
      onError(err.message || 'Failed to fetch metadata');
    } finally {
      setFetching(false);
    }
  };

  const handleUploadMetadata = async () => {
    if (!metadataFile) {
      onError('Please choose a metadata XML file to upload');
      return;
    }

    setUploading(true);
    try {
      const response = await samlAPI.uploadSPMetadata(appId, metadataFile);
      onSuccess(`Metadata uploaded! Found ${response.attributes_detected} attributes`);
      // Clear file selection after successful upload
      setMetadataFile(null);
      // Reload SP metadata and attributes
      loadData();
    } catch (err) {
      onError(err.message || 'Failed to upload metadata');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadMetadata = async () => {
    try {
      const metadata = await samlAPI.getIdPMetadata(appId);
      const blob = new Blob([metadata], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `idp-metadata-app${appId}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      onSuccess('Metadata downloaded successfully');
    } catch (err) {
      onError(err.message || 'Failed to download metadata');
    }
  };

  const tabs = [
    { id: 'sp-metadata', label: 'SP Configuration', icon: '⚙️' },
    { id: 'metadata', label: 'IdP Metadata', icon: '📄' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium">SAML Configuration</h3>
          <p className="text-sm text-gray-500 mt-1">Configure SAML 2.0 Identity Provider settings</p>
        </div>

        {/* Sub-Tabs */}
        <div className="border-b px-6">
          <nav className="flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-2 text-gray-600">Loading...</p>
            </div>
          )}

          {!loading && activeTab === 'sp-metadata' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-primary">
                  <strong>Service Provider Configuration:</strong> Enter the SAML details from your Service Provider (e.g., Zoho Desk).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entity ID (Issuer) *
                </label>
                <input
                  type="text"
                  value={spMetadata.entity_id}
                  onChange={(e) => setSPMetadata({ ...spMetadata, entity_id: e.target.value })}
                  placeholder="e.g., tickets.backups.net.au"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                />
                <p className="mt-1 text-xs text-gray-500">The unique identifier for the Service Provider</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assertion Consumer Service (ACS) URL *
                </label>
                <input
                  type="text"
                  value={spMetadata.acs_url}
                  onChange={(e) => setSPMetadata({ ...spMetadata, acs_url: e.target.value })}
                  placeholder="https://accounts.zohoportal.com.au/accounts/csamlresponse/..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                />
                <p className="mt-1 text-xs text-gray-500">Where SAML responses will be sent</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Single Logout (SLO) URL
                </label>
                <input
                  type="text"
                  value={spMetadata.slo_url || ''}
                  onChange={(e) => setSPMetadata({ ...spMetadata, slo_url: e.target.value })}
                  placeholder="https://accounts.zohoportal.com.au/accounts/logout"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                />
                <p className="mt-1 text-xs text-gray-500">Optional: URL for logout requests</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  NameID Format
                </label>
                <select
                  value={spMetadata.name_id_format}
                  onChange={(e) => setSPMetadata({ ...spMetadata, name_id_format: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                >
                  <option value="urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress">Email Address</option>
                  <option value="urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified">Unspecified</option>
                  <option value="urn:oasis:names:tc:SAML:2.0:nameid-format:persistent">Persistent</option>
                  <option value="urn:oasis:names:tc:SAML:2.0:nameid-format:transient">Transient</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Signing Certificate *
                </label>
                <select
                  value={samlConfig.certificate_id || ''}
                  onChange={(e) => setSAMLConfig({ ...samlConfig, certificate_id: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select a certificate...</option>
                  {certificates.map(cert => (
                    <option key={cert.id} value={cert.id}>
                      {cert.name} (Valid until: {new Date(cert.valid_until).toLocaleDateString()})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Certificate used to sign SAML responses for this application</p>
              </div>

              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={samlConfig.sign_assertions}
                    onChange={(e) => setSAMLConfig({ ...samlConfig, sign_assertions: e.target.checked })}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Sign SAML Assertions</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={samlConfig.sign_response}
                    onChange={(e) => setSAMLConfig({ ...samlConfig, sign_response: e.target.checked })}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Sign SAML Response</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={samlConfig.encrypt_assertions}
                    onChange={(e) => setSAMLConfig({ ...samlConfig, encrypt_assertions: e.target.checked })}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Encrypt SAML Assertions</span>
                </label>
              </div>

              {samlConfig.encrypt_assertions && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SP Encryption Certificate
                    </label>
                    <select
                      value={samlConfig.encryption_certificate_id || ''}
                      onChange={(e) => setSAMLConfig({ ...samlConfig, encryption_certificate_id: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select SP certificate...</option>
                      {certificates.map(cert => (
                        <option key={cert.id} value={cert.id}>
                          {cert.name} (Valid until: {new Date(cert.valid_until).toLocaleDateString()})
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      The SP's public certificate used to encrypt assertions. The SP must have the matching private key to decrypt.
                    </p>
                  </div>
                </div>
              )}

              {/* Metadata URL Fetching */}
              <div className="border-t pt-6">
                <h4 className="font-medium mb-4">Or Configure from SP Metadata</h4>
                <div className="space-y-6">
                  {/* Fetch by URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SP Metadata URL
                    </label>
                    <input
                      type="url"
                      value={metadataUrl}
                      onChange={(e) => setMetadataUrl(e.target.value)}
                      placeholder="https://desk.zoho.com/saml/metadata/12345"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Automatically configure by fetching SP metadata from a URL
                    </p>
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={handleAutoDetect}
                        disabled={autoDetecting || !spMetadata.entity_id || !spMetadata.acs_url}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                      >
                        {autoDetecting ? 'Detecting...' : '🔍 Auto-Detect'}
                      </button>
                      <button
                        onClick={handleFetchMetadata}
                        disabled={fetching || !metadataUrl}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                      >
                        {fetching ? 'Fetching...' : '📥 Fetch Metadata'}
                      </button>
                    </div>
                  </div>

                  {/* Upload XML file */}
                  <div className="border-t pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Or Upload SP Metadata XML
                    </label>
                    <input
                      type="file"
                      accept=".xml,text/xml,application/xml"
                      onChange={(e) => setMetadataFile(e.target.files[0] || null)}
                      className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary-light file:text-primary hover:file:bg-primary-light"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Upload the metadata XML file downloaded from your Service Provider (e.g., Zoho Desk).
                    </p>
                    <button
                      onClick={handleUploadMetadata}
                      disabled={uploading || !metadataFile}
                      className="mt-3 px-4 py-2 bg-primary text-white rounded hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploading ? 'Uploading...' : '📤 Upload Metadata XML'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="pt-4 border-t flex justify-end">
                <button
                  onClick={handleSaveSPMetadata}
                  disabled={!spMetadata.entity_id || !spMetadata.acs_url || loading}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : 'Save SP Configuration'}
                </button>
              </div>
            </div>
          )}

          {!loading && activeTab === 'metadata' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-primary">
                  <strong>IdP Metadata:</strong> Download this metadata XML file and upload it to your Service Provider.
                </p>
              </div>

              <div className="border rounded-lg p-6 text-center">
                <div className="mb-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-light rounded-full">
                    <span className="text-3xl">📄</span>
                  </div>
                </div>
                <h4 className="font-medium mb-2">IdP Metadata XML</h4>
                <p className="text-sm text-gray-600 mb-4">
                  This file contains your IdP's public certificate, SSO URL, and other configuration details.
                </p>
                <button
                  onClick={handleDownloadMetadata}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover"
                >
                  Download Metadata
                </button>
              </div>

              <div className="border rounded-lg p-4 bg-gray-50">
                <h4 className="font-medium mb-3">Configuration URLs</h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <strong className="text-gray-700">SSO URL:</strong>
                    <code className="ml-2 text-xs bg-white px-2 py-1 rounded">
                      {window.location.origin}/saml/sso/{appId}
                    </code>
                  </div>
                  <div>
                    <strong className="text-gray-700">SLO URL:</strong>
                    <code className="ml-2 text-xs bg-white px-2 py-1 rounded">
                      {window.location.origin}/saml/slo/{appId}
                    </code>
                    <span className="ml-2 text-xs text-gray-500">(SAML 2.0 protocol)</span>
                  </div>
                  <div>
                    <strong className="text-gray-700">Simple Logout URL:</strong>
                    <code className="ml-2 text-xs bg-white px-2 py-1 rounded">
                      {window.location.origin}/api/saml/logout
                    </code>
                    <span className="ml-2 text-xs text-gray-500">(For SPs without SAML SLO support)</span>
                  </div>
                  <div>
                    <strong className="text-gray-700">Entity ID:</strong>
                    <code className="ml-2 text-xs bg-white px-2 py-1 rounded">
                      {window.location.origin}/saml
                    </code>
                  </div>
                  <div>
                    <strong className="text-gray-700">Metadata URL:</strong>
                    <code className="ml-2 text-xs bg-white px-2 py-1 rounded">
                      {window.location.origin}/saml/metadata/{appId}
                    </code>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SAMLConfiguration;
